import { useParams, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '../services/api'
import DOMPurify from 'dompurify'
import { getMediaUrl } from '../utils/media'
import { 
  ChevronLeft, 
  MapPin, 
  Calendar, 
  Users, 
  BookOpen, 
  Building2, 
  Loader2,
  Image as ImageIcon,
  CheckCircle2,
  GraduationCap
} from 'lucide-react'

const DetailProgramme = () => {
  const { id } = useParams()

  const { data: programme, isLoading, error } = useQuery({
    queryKey: ['programme', id],
    queryFn: async () => {
      const res = await api.get(`/programmes/${id}`)
      return res.data
    }
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <Loader2 className="animate-spin text-cidh-blue mb-4" size={50} />
        <p className="text-gray-500 font-medium">Chargement des détails du programme...</p>
      </div>
    )
  }

  if (error || !programme) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">Oups ! Programme introuvable.</h2>
        <Link to="/programmes" className="text-cidh-blue hover:underline flex items-center gap-2">
          <ChevronLeft size={20} /> Retour à la liste des programmes
        </Link>
      </div>
    )
  }

  const coverImage = getMediaUrl(programme.image_couverture, 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?q=80&w=1200')

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Date non définie';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  }

  return (
    <div className="pt-32 pb-16 bg-gray-50 min-h-screen">
      <div className="container mx-auto px-4">
        <Link to="/programmes" className="inline-flex items-center gap-2 text-cidh-blue mb-8 hover:translate-x-1 transition-transform font-semibold">
          <ChevronLeft size={20} /> Retour aux formations
        </Link>

        <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100">
          {/* Header Section */}
          <div className="relative h-[400px]">
            <img 
              src={coverImage} 
              alt={programme.titre}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-cidh-blue/90 via-cidh-blue/40 to-transparent"></div>
            <div className="absolute bottom-0 left-0 p-8 md:p-12 w-full">
              <div className="flex flex-wrap gap-3 mb-4">
                <span className="bg-cidh-gold text-cidh-blue px-4 py-1.5 rounded-full font-bold text-xs uppercase tracking-wider">
                  {programme.categorie}
                </span>
                <span className="bg-white/20 backdrop-blur-md text-white px-4 py-1.5 rounded-full font-medium text-xs uppercase tracking-wider border border-white/20">
                  {programme.statut}
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white font-montserrat tracking-tight leading-tight">
                {programme.titre}
              </h1>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-0">
            {/* Main Content */}
            <div className="lg:col-span-2 p-8 md:p-12 border-r border-gray-100">
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-cidh-blue mb-6 flex items-center gap-3">
                  <div className="w-1 h-8 bg-cidh-gold rounded-full"></div>
                  Présentation du programme
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed mb-8 italic">
                  {programme.description_courte}
                </p>
                <div 
                  className="prose prose-lg max-w-none text-gray-700 prose-headings:text-cidh-blue prose-strong:text-cidh-blue"
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(programme.contenu_riche) }}
                />
              </section>

              {programme.partenaire && (
                <section className="bg-cidh-gray-light p-8 rounded-3xl border border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-sm border border-gray-50">
                      <Building2 size={30} className="text-cidh-blue" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-gray-400 uppercase tracking-widest">Partenaire Associé</h4>
                      <p className="text-xl font-bold text-cidh-blue">{programme.partenaire}</p>
                    </div>
                  </div>
                </section>
              )}
            </div>

            {/* Sidebar Details */}
            <div className="bg-gray-50/50 p-8 md:p-12 space-y-8">
              <div>
                <h3 className="text-lg font-bold text-cidh-blue mb-6 pb-2 border-b border-gray-200">Informations Clés</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="bg-white p-3 rounded-xl shadow-sm"><Calendar size={20} className="text-cidh-gold" /></div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase">Période</p>
                      <p className="text-gray-800 font-semibold">
                        Du {formatDate(programme.date_debut)}<br/>
                        Au {formatDate(programme.date_fin)}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="bg-white p-3 rounded-xl shadow-sm"><MapPin size={20} className="text-cidh-gold" /></div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase">Lieu</p>
                      <p className="text-gray-800 font-semibold">{programme.lieu || 'Non spécifié'}</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-white p-3 rounded-xl shadow-sm"><Users size={20} className="text-cidh-gold" /></div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase">Bénéficiaires</p>
                      <p className="text-gray-800 font-semibold">{programme.beneficiaires} personnes formées</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="bg-white p-3 rounded-xl shadow-sm"><GraduationCap size={20} className="text-cidh-gold" /></div>
                    <div>
                      <p className="text-xs font-bold text-gray-400 uppercase">Catégorie</p>
                      <p className="text-gray-800 font-semibold">{programme.categorie}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="pt-8">
                <Link 
                  to="/contact" 
                  className="w-full bg-cidh-blue text-white font-bold py-4 rounded-2xl flex items-center justify-center gap-3 shadow-lg shadow-cidh-blue/20 hover:scale-[1.02] active:scale-95 transition-all text-center"
                >
                  S'inscrire à cette formation
                </Link>
                <p className="text-xs text-center text-gray-400 mt-4 leading-relaxed">
                  Des questions ? Contactez nos experts pour un accompagnement personnalisé.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DetailProgramme
